import type { RefObject } from "react";

import { useIsomorphicLayoutEffect } from "@shared/hooks/useIsomorphicLayoutEffect/useIsomorphicLayoutEffect";

/* "pointerdown" fires before any React re-render triggered by the press. Use it
   when the press can reparent the pressed element (e.g. the catalog search input
   moving into the takeover header): iOS retargets the later compat "click" to the
   document root once the original node is detached, which reads as an outside
   click and instantly closes what the tap just opened. */
const useOutsideClick = <T extends HTMLElement>(
  ref: RefObject<T | null> | ReadonlyArray<RefObject<T | null> | null> | null,
  onClick: (e: MouseEvent) => void,
  eventType: "click" | "pointerdown" = "click"
) => {
  useIsomorphicLayoutEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const refs = Array.isArray(ref) ? ref : [ref];
      const elements = refs.flatMap(r => (r?.current ? [r.current] : []));
      if (elements.length > 0 && !elements.some(el => el.contains(e.target as Node))) {
        onClick(e);
      }
    };
    document.addEventListener(eventType, handleClick, { capture: true });

    return () => {
      document.removeEventListener(eventType, handleClick, { capture: true });
    };
  }, [onClick, eventType]);
};

export { useOutsideClick };
